// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import {
  rowValidationErrorSchema,
  validationErrorFieldSchema,
} from '@/csv-import/schemas';
import { z } from 'zod';

const processedCostUpdateItemSchema = z.object({
  index: z.number(),
  key: z.string(),
  updated: z.boolean(),
  noOp: z.boolean(),
  fieldsUpdated: z
    .record(z.boolean())
    .nullable()
    .transform((value) => value ?? {}),
  errors: z
    .array(validationErrorFieldSchema)
    .nullable()
    .transform((value) => value ?? []),
});

const costDataUpdateResultSchema = z.object({
  success: z.boolean(),
  items: z.array(processedCostUpdateItemSchema),
  structureErrors: z.array(validationErrorFieldSchema),
  rowErrors: z.array(rowValidationErrorSchema),
});

const uploadCostDataUpdateResponseSchema = z.object({
  data: costDataUpdateResultSchema,
  metadata: responseMetadataSchema,
});

type CostDataUpdateResult = z.infer<typeof costDataUpdateResultSchema>;

export function parseCostDataUpdateErrorResponse(
  data: unknown
): CostDataUpdateResult | null {
  const parsed = uploadCostDataUpdateResponseSchema.safeParse(data);
  return parsed.success ? parsed.data.data : null;
}

export async function uploadCostDataUpdate(
  formData: FormData
): Promise<CostDataUpdateResult> {
  const url = '/v1/import/cost-data-update';
  const response = await api.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  const parsed = await parseResponse(
    uploadCostDataUpdateResponseSchema,
    response.data,
    {
      schemaName: 'UploadCostDataUpdate',
      endpoint: url,
    }
  );
  return parsed.data;
}
